import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr } from "@/lib/format";
import { Skeleton, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const SettlementSku = () => {
  const [options, setOptions] = useState(null);
  const [settlementId, setSettlementId] = useState("");
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [syncing, setSyncing] = useState(false);

  const loadOptions = () => api.get("/tools/settlement-sku/options").then(({ data }) => {
    setOptions(data);
    setSyncing(!!data.state?.running);
    if (data.settlements.length && !settlementId) setSettlementId(data.settlements[0].settlement_id);
  });
  useEffect(() => { loadOptions(); }, []);

  useEffect(() => {
    if (!settlementId) return;
    setData(null);
    api.get(`/tools/settlement-sku?settlement_id=${settlementId}`).then(({ data }) => setData(data));
  }, [settlementId]);

  useEffect(() => {
    if (!syncing) return;
    const t = setInterval(async () => {
      const { data: d } = await api.get("/tools/settlement-sku/options");
      if (!d.state?.running) {
        clearInterval(t);
        setSyncing(false);
        if (d.state?.error) toast.error(`Settlement report sync failed: ${d.state.error}`);
        else toast.success(`Settlement reports loaded — ${d.settlements.length} cycles available`);
        setOptions(d);
        if (d.settlements.length && !settlementId) setSettlementId(d.settlements[0].settlement_id);
      }
    }, 8000);
    return () => clearInterval(t);
  }, [syncing, settlementId]);

  const sync = async () => {
    await api.post("/tools/settlement-sku/sync");
    toast.info("Fetching settlement reports from Amazon — a few minutes…");
    setSyncing(true);
  };

  if (!options) return <Skeleton className="h-64" />;
  const setts = options.settlements || [];

  return (
    <div className="space-y-5" data-testid="settlement-sku">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Per-SKU breakdown of an actual Amazon settlement — gross sales, fees, promos and refunds as Amazon settled them,
          straight from the official settlement report.
        </p>
        <div className="flex gap-2 items-center">
          {setts.length > 0 && (
            <Select value={settlementId} onValueChange={setSettlementId}>
              <SelectTrigger className="w-72 h-9 rounded-full text-xs" data-testid="settlement-select"><SelectValue placeholder="Select settlement" /></SelectTrigger>
              <SelectContent>
                {setts.map((s) => (
                  <SelectItem key={s.settlement_id} value={s.settlement_id} data-testid={`settlement-opt-${s.settlement_id}`}>
                    {s.start} → {s.end} · payout {inr(s.total_amount)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" className="rounded-full" onClick={sync} disabled={syncing} data-testid="settlement-sync-button">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} aria-hidden="true" />
            {syncing ? "Fetching…" : setts.length ? "Refresh reports" : "Fetch settlement reports"}
          </Button>
        </div>
      </div>

      {setts.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground bg-card border border-border rounded-lg">
          No settlement reports ingested yet — click "Fetch settlement reports". Amazon generates these automatically each settlement cycle (~2 weeks); we pull the last ~3 months.
        </p>
      ) : !data ? <Skeleton className="h-64" /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Gross sales" value={inr(data.totals.gross)} sub={`${data.skus.length} SKUs`} testId="ss-gross-kpi" />
            <StatCard label="Amazon fees" value={inr(Math.abs(data.totals.fees))} sub={data.totals.gross ? `${Math.abs(data.totals.fees / data.totals.gross * 100).toFixed(1)}% of gross` : ""} testId="ss-fees-kpi" />
            <StatCard label="Refunds" value={inr(Math.abs(data.totals.refunds))} testId="ss-refunds-kpi" />
            <StatCard label="Account-level (no SKU)" value={data.account_level ? inr(data.account_level.net) : "₹0"} sub="storage, ads, subscriptions…" testId="ss-account-kpi" />
            <StatCard label="Net payout" value={inr(data.totals.net)} sub={data.settlement ? `deposited ${data.settlement.deposit_date || "—"}` : ""} testId="ss-net-kpi" />
          </div>
          <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="ss-table-card">
            <div className="p-3 border-b border-border">
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search by product, SKU or ASIN…" data-testid="ss-search-input"
                className="w-full max-w-sm h-9 rounded-full border border-border bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <table className="w-full text-sm" data-testid="ss-table">
              <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>{["Product", "SKU", "ASIN", "Units", "Gross", "Fees", "Promos", "Refunds", "Net", "Net / unit"].map((h) =>
                  <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.skus.filter((r) => ((r.name || "") + r.sku + (r.asin || "")).toLowerCase().includes(filter.toLowerCase())).slice(0, 400).map((r) => (
                  <tr key={r.sku} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`ss-row-${r.sku}`}>
                    <td className="px-4 py-2.5 font-medium max-w-xs truncate">{r.name || r.sku}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{r.sku}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{r.asin}</td>
                    <td className="px-4 py-2.5">{r.units}</td>
                    <td className="px-4 py-2.5">{inr(r.gross)}</td>
                    <td className="px-4 py-2.5 text-red-600">{inr(r.fees)}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{inr(r.promos)}</td>
                    <td className="px-4 py-2.5 text-red-600">{inr(r.refunds)}</td>
                    <td className={`px-4 py-2.5 font-heading font-bold ${r.net < 0 ? "text-red-600" : ""}`}>{inr(r.net)}</td>
                    <td className="px-4 py-2.5">{r.net_per_unit != null ? inr(r.net_per_unit) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
